// Single source of truth for verified business facts
// Sources: storefront photos (in-build), fit360life.com, Google Business listing,
// Vagaro booking page, fit360life Facebook page, fitnesstraining360 Instagram (Dec 2025).
export const SITE = {
  name: 'Fitness Training 360',
  shortName: 'FT360',
  owner: 'Jarad Wingard',
  ownerRole: 'Owner & NASM-Certified Personal Trainer',
  tagline: 'Private Personal Training in Stuart, FL',
  mission: 'It’s about using 360 degrees of your body. Our mission is to improve the quality of your life while helping you reach whatever health goals you may have.',
  city: 'Stuart',
  county: 'Martin County',
  state: 'Florida',
  stateAbbr: 'FL',
  address: {
    line1: '3850 SE Dixie Highway',
    line2: 'Stuart, FL 34997',
    full: '3850 SE Dixie Highway, Stuart, FL 34997',
    note: 'By Appointment Only',
  },
  phone: '772-400-5743',
  phoneE164: '+17724005743',
  sms: '772-400-5743',
  email: 'ft360lifemgt@gmail.com',
  domain: 'fit360life.com',
  publicUrl: 'https://fit360life.com',
  // Verified business hours from Google business listing.
  // Studio is by-appointment only; sessions scheduled during these hours.
  hoursLabel: 'By appointment · 7 days a week',
  hoursDetail: 'Sessions scheduled 5:00 AM – 8:00 PM, seven days a week.',  hoursStructured: [
    { day: 'Sunday', open: '05:00', close: '20:00' },
    { day: 'Monday', open: '05:00', close: '20:00' },
    { day: 'Tuesday', open: '05:00', close: '20:00' },
    { day: 'Wednesday', open: '05:00', close: '20:00' },
    { day: 'Thursday', open: '05:00', close: '20:00' },
    { day: 'Friday', open: '05:00', close: '20:00' },
    { day: 'Saturday', open: '05:00', close: '20:00' },
  ],
  geo: { lat: 27.1689, lng: -80.2114 },
  logo: {
    src: '/logo.png',
    alt: 'Fitness Training 360 — Private Personal Training Studio in Stuart, FL',
    width: 1536,
    height: 1024,
  },
  // Verified credential: NASM-Certified Personal Trainer (per Google listing + Instagram bio).
  credentials: [
    { label: 'NASM-Certified Personal Trainer', short: 'NASM-CPT' },
  ],
  // External booking + social
  booking: {
    label: 'Book on Vagaro',
    url: 'https://www.vagaro.com/fitnesstraining360',
  },
  // Google Business Profile (verified)
  googleBusinessProfile: 'https://share.google/dr6YSQ17tjiCBBt55',
  googleWriteReview: 'https://share.google/dr6YSQ17tjiCBBt55',
  social: {
    facebook: 'https://www.facebook.com/fit360life/',
    facebookOwner: 'https://www.facebook.com/jarad.wingard/',
    instagram: 'https://www.instagram.com/fitnesstraining360/',
  },
};

export const PHOTOS = {
  storefrontWithOwner: 'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/odo0fjd0_IMG_0220.jpeg',
  coachingClient: 'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/jn9vslkw_IMG_0221.jpeg',
  jaradAtDesk: 'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/4jgi4bn3_IMG_0222.jpeg',
  jaradWithStaffStorefront: 'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/b8emmccl_IMG_0223.jpeg',
  jaradFrontDoor: 'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/seepc1jx_IMG_0224.jpeg',
};

// Studio gallery — hosted from /public/gallery, converted from owner-provided HEIC/PNG originals.
// Used on the homepage "See our studio" slideshow and on the /facility page.
export const STUDIO_GALLERY = [
  { src: '/gallery/studio-01.jpg', alt: `Inside the private ${SITE.name} studio — training floor and equipment in ${SITE.city}, ${SITE.stateAbbr}` },
  { src: '/gallery/studio-02.jpg', alt: `Free weights and rack area at the ${SITE.name} private personal training studio` },
  { src: '/gallery/studio-03.jpg', alt: `Functional training space at the ${SITE.name} private studio in ${SITE.county}` },
  { src: '/gallery/studio-04.jpg', alt: `Cable and accessory equipment inside the ${SITE.name} studio in ${SITE.city}, ${SITE.stateAbbr}` },
  { src: '/gallery/studio-05.jpg', alt: `Open training floor at the ${SITE.name} private personal training studio` },
  { src: '/gallery/studio-06.jpg', alt: `${SITE.name} — private personal training studio branding and interior` },
];

// Real client transformations — published only with client consent.
// Every result is verified by the coach (Jarad Wingard) and the client.
export const TRANSFORMATIONS = [
  {
    id: 'terry-longo',
    clientName: 'Terry Longo',
    location: `${SITE.city}, ${SITE.stateAbbr}`,
    focus: 'Weight loss & strength/conditioning',
    durationLabel: '7 months',
    beforeImage: {
      src: '/gallery/transformation-terry-before.jpg',
      alt: 'Terry Longo before — client of Fitness Training 360 in Stuart, FL, at the start of her personal training journey',
    },
    afterImage: {
      src: '/gallery/transformation-terry-after.jpg',
      alt: 'Terry Longo after — 40 lbs down and 6 inches off the waist in 7 months with private personal training at Fitness Training 360 in Stuart, FL',
    },
    metrics: [
      { label: 'Weight lost', value: '40 lbs' },
      { label: 'Off the waist', value: '6 inches' },
      { label: 'Timeframe', value: '7 months' },
    ],
    approach: [
      'Nutrition overhaul — sustainable habit-based eating (no crash dieting)',
      'Consistent training — private 1-on-1 sessions focused on strength and conditioning',
      'Weekly accountability with her coach at the studio in Stuart, FL',
    ],
    // "Coach summary" — a factual paraphrase from Jarad, NOT fabricated as a direct client quote.
    coachSummary: 'Terry changed her diet, started moving her body consistently, and stayed with the plan. Along the way she felt better, looked better, and built real confidence in herself.',
    consent: true,
  },
];

export const SERVICES = [
  { slug: 'personal-training', title: 'Personal Training', short: '1-on-1 coaching in a private Stuart gym. Real attention, real programming, real accountability.', icon: 'Dumbbell' },
  { slug: 'semi-private-personal-training', title: 'Semi-Private Personal Training', short: 'Train with a partner or in a small group. Individual programming inside one shared session.', icon: 'Users' },
  { slug: 'nutrition-coaching', title: 'Nutrition Coaching', short: 'Habit-based nutrition coaching to support your training and your lifestyle.', icon: 'Apple' },
  { slug: 'golf-fitness-training', title: 'Golf Fitness Training', short: 'Mobility, rotation, and strength for golfers across Martin County.', icon: 'Target' },
  { slug: 'online-fitness-coaching', title: 'Online Fitness Coaching', short: 'Personalized programming and check-ins when you can’t train in person.', icon: 'Laptop' },
];

export const LOCATIONS = [
  { slug: 'palm-city-fl', name: 'Palm City', drive: '~10 min', context: 'A short drive across the Roosevelt Bridge from Palm City to our private Stuart studio.' },
  { slug: 'jensen-beach-fl', name: 'Jensen Beach', drive: '~15 min', context: 'Quiet coastal community just north of Stuart with easy access down US-1.' },
  { slug: 'hobe-sound-fl', name: 'Hobe Sound', drive: '~20 min', context: 'South Martin County community a straight shot up US-1 to our studio.' },
  { slug: 'port-salerno-fl', name: 'Port Salerno', drive: '~7 min', context: 'Working waterfront neighbors of our studio on SE Dixie Highway.' },
  { slug: 'rio-fl', name: 'Rio', drive: '~10 min', context: 'Small Martin County community just north of the Stuart studio.' },
  { slug: 'indiantown-fl', name: 'Indiantown', drive: '~30 min', context: 'Western Martin County. We support Indiantown clients in person and via online coaching.' },
  { slug: 'sewalls-point-fl', name: 'Sewall’s Point', drive: '~10 min', context: 'Peninsula community connected to Stuart via the Evans Crary Bridge.' },
  { slug: 'ocean-breeze-fl', name: 'Ocean Breeze', drive: '~12 min', context: 'Quiet enclave between Jensen Beach and Stuart.' },
  { slug: 'north-river-shores-fl', name: 'North River Shores', drive: '~12 min', context: 'Established residential community north of downtown Stuart.' },
  { slug: 'golden-gate-fl', name: 'Golden Gate', drive: '~10 min', context: 'Stuart-area neighborhood near our private studio.' },
  { slug: 'south-stuart-fl', name: 'South Stuart', drive: '~5 min', context: 'Our own neighborhood. The studio is on SE Dixie Highway in south Stuart.' },
];

export const NAV = [
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about-jarad-wingard' },
  { label: 'Transformations', href: '/client-transformations' },
  { label: 'Reviews', href: '/reviews' },
  { label: 'Facility', href: '/facility' },
  { label: 'Locations', href: '/locations' },
  { label: 'Contact', href: '/contact' },
];
